'use client';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { MPS_EVENTS, MPSEvent } from '@/data/scheduleData';

interface EventContextType {
  selectedEvent: MPSEvent;
  setSelectedEvent: (event: MPSEvent) => void;
  allEvents: MPSEvent[];
}

const EventContext = createContext<EventContextType>({
  selectedEvent: MPS_EVENTS[0],
  setSelectedEvent: () => {},
  allEvents: MPS_EVENTS,
});

export const useEvent = () => useContext(EventContext);

const EVENT_STORAGE_KEY = 'mps_selected_event';

function findCurrentEvent(): MPSEvent | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    MPS_EVENTS.find(event => {
      const start = new Date(event.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(event.endDate);
      end.setHours(23, 59, 59, 999);
      return today >= start && today <= end;
    }) ?? null
  );
}

export function EventProvider({ children }: { children: React.ReactNode }) {
  const [selectedEvent, setSelectedEventState] = useState<MPSEvent>(MPS_EVENTS[0]);

  useEffect(() => {
    // Priority 1: current event based on today's date
    const currentEvent = findCurrentEvent();
    if (currentEvent) {
      setSelectedEventState(currentEvent);
      try {
        localStorage.setItem(EVENT_STORAGE_KEY, currentEvent.id);
      } catch {}
      return;
    }

    // Priority 2: last manually selected event from localStorage
    try {
      const stored = localStorage.getItem(EVENT_STORAGE_KEY);
      if (stored) {
        const found = MPS_EVENTS.find(e => e.id === stored);
        if (found) {
          setSelectedEventState(found);
          return;
        }
      }
    } catch {}

    // Priority 3: default to first event
    setSelectedEventState(MPS_EVENTS[0]);
  }, []);

  const setSelectedEvent = (event: MPSEvent) => {
    setSelectedEventState(event);
    try {
      localStorage.setItem(EVENT_STORAGE_KEY, event.id);
    } catch {}
  };

  return (
    <EventContext.Provider value={{ selectedEvent, setSelectedEvent, allEvents: MPS_EVENTS }}>
      {children}
    </EventContext.Provider>
  );
}
